import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr';
import './style/MyCampaigns.css';
import { useNavigate } from 'react-router-dom';

// --- Custom Select (Reused) ---
const CustomSelect = ({ options, value, onChange, placeholder, labelKey = 'name', valueKey = 'id' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedItem = options.find(o => o[valueKey] === value);

    return (
        <div className={`mc-select-wrapper ${isOpen ? 'open' : ''}`} ref={containerRef}>
            <div className={`mc-select-toggle ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                <span>{selectedItem ? selectedItem[labelKey] : placeholder}</span>
                <span className="mc-arrow-icon">▼</span>
            </div>
            {isOpen && (
                <div className="mc-select-menu">
                    {options.map((opt) => (
                        <div
                            key={opt[valueKey]}
                            className={`mc-select-item ${value === opt[valueKey] ? 'selected' : ''}`}
                            onClick={() => { onChange(opt[valueKey]); setIsOpen(false); }}
                        >
                            <span>{opt[labelKey]}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


const MyCampaigns = () => {
    // --- State ---
    const [loading, setLoading] = useState(true);
    const [campaigns, setCampaigns] = useState([]);
    const [domains, setDomains] = useState([]);
    const [user, setUser] = useState({});
    const navigate = useNavigate();
    // Edit Modal State
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- Fetch Data ---
    const fetchData = async () => {
        setLoading(true);
        const adminToken = getCookie('authToken');
        try {
            // 1. Fetch Campaigns
            const campRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'fetchPublisherCampaigns',
                authToken: adminToken
            });

            // 2. Fetch Domains
            const domainRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'fetchDomains',
                authToken: adminToken
            });

            if (campRes.data.status) setCampaigns(campRes.data.data);

            if (domainRes.data.status) setDomains(domainRes.data.domains);

        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const authToken = getCookie('authToken');
        const fetchDatas = async () => {
            try {
                setLoading(true);
                const userResponse = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuth',
                    authToken
                });

                if (userResponse.data.status === false) {
                    toast.error(userResponse.data.message || 'Session expired', 3000);
                    navigate('/login');
                    return;
                } else {
                    setUser(userResponse.data.res);
                    await fetchData();
                }
            } catch (error) {
                console.error(error);
                toast.error('Failed to load dashboard data');
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };
        fetchDatas();

    }, []);

    // --- Actions ---
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Link Copied!");
    };

    const handleToggleStatus = async (id, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'paused' : 'active';
        setCampaigns(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));

        try {
            await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'updateCampaignStatus',
                authToken: getCookie('authToken'),
                campaign_id: id,
                status: newStatus
            });
            toast.success(`Campaign ${newStatus}`);
        } catch (error) {
            toast.error("Failed to update status");
            setCampaigns(prev => prev.map(c => c.id === id ? { ...c, status: currentStatus } : c));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure? This cannot be undone.")) return;

        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'deleteCampaign',
                authToken: getCookie('authToken'),
                campaign_id: id
            });

            if (res.data.status) {
                setCampaigns(prev => prev.filter(c => c.id !== id));
                toast.success("Campaign Deleted");
            } else {
                toast.error("Delete Failed");
            }
        } catch (error) {
            toast.error("Server Error");
        }
    };

    // --- Edit Logic ---
    const openEditModal = (camp) => {
        setEditData({
            id: camp.id,
            title: camp.meta.title || '',
            telegram: camp.meta.telegram || '',
            refer_title: camp.refer_meta?.title || '',
            refer_name: camp.refer_meta?.name || '',
            domain_id: camp.domain_id || '',
            // Ensure deep copy
            payouts: camp.payouts_meta ? JSON.parse(JSON.stringify(camp.payouts_meta)) : []
        });
        setIsEditModalOpen(true);
    };

    // --- STRICT VALIDATION LOGIC ---
    const handlePayoutChange = (index, type, val) => {
        // Allow empty string for typing
        if (val === '') {
            const newPayouts = [...editData.payouts];
            newPayouts[index][type] = '';
            setEditData(prev => ({ ...prev, payouts: newPayouts }));
            return;
        }

        const numericVal = parseFloat(val);
        if (isNaN(numericVal) || numericVal < 0) return;

        const newPayouts = [...editData.payouts];
        const currentEvent = newPayouts[index];
        const limit = parseFloat(currentEvent.limit) || 0;

        // Get the value of the OTHER field to calculate total
        const otherType = type === 'user' ? 'refer' : 'user';
        const otherVal = parseFloat(currentEvent[otherType]) || 0;

        // VALIDATION: Check if sum exceeds limit
        if (numericVal + otherVal > limit) {
            toast.warning(`Total payout cannot exceed Limit: ₹${limit}`);
            return; // Block the update
        }

        // If valid, update state
        newPayouts[index][type] = numericVal;
        setEditData(prev => ({ ...prev, payouts: newPayouts }));
    };

    const handleEditSave = async () => {
        setIsSaving(true);
        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'editCampaign',
                authToken: getCookie('authToken'),
                campaign_id: editData.id,
                updated_data: {
                    meta: { title: editData.title, telegram: editData.telegram },
                    refer_meta: { title: editData.refer_title, name: editData.refer_name },
                    domain_id: editData.domain_id,
                    payout_config: editData.payouts
                }
            });

            if (res.data.status) {
                toast.success("Campaign Updated");
                // Update local state deeply
                setCampaigns(prev => prev.map(c => c.id === editData.id ? {
                    ...c,
                    meta: { title: editData.title, telegram: editData.telegram },
                    refer_meta: { title: editData.refer_title, name: editData.refer_name },
                    domain_id: editData.domain_id,
                    payouts_meta: editData.payouts
                } : c));
                setIsEditModalOpen(false);
            } else {
                toast.error("Update Failed");
            }
        } catch (error) {
            toast.error("Server Error");
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="mc-wrapper_mc">
           <Navbar name={user?.name || 'Partner'} role={user?.role || 'Publisher'} />

            <div className="mc-bg-orb mc-orb-1"></div>
            <div className="mc-bg-orb mc-orb-2"></div>

            <div className="mc-container_mc">

                <div className="mc-header_mc">
                    <h2>My <span className="mc-highlight">Campaigns</span></h2>
                    <p>Manage your active tracking links and performance</p>
                </div>

                <div className="mc-card_mc">
                    <div className="mc-table-scroll_mc">
                        <table className="mc-table_mc">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Offer</th>
                                    <th className="text-right_mc">Total Payout</th>
                                    <th>Campaign Link</th>
                                    <th>Refer Link</th>
                                    <th className="text-center_mc">Status</th>
                                    <th className="text-center_mc sticky-col_mc">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.length > 0 ? (
                                    campaigns.map((row) => (
                                        <tr key={row.id} className="mc-row_mc">
                                            <td className="mono_mc">{row.id}</td>
                                            <td>
                                                <div className="mc-offer-flex">
                                                    <img src={row.logo} alt="" className="mc-mini-logo" />
                                                    <div className="mc-offer-info">
                                                        <span className="mc-offer-name">{row.offer_name}</span>
                                                        <span className="mc-offer-id">{row.offer_id}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-right_mc font-bold_mc mc-payout-text">₹{row.total_payout}</td>

                                            <td>
                                                <div className="mc-copy-pill" onClick={() => handleCopy(row.campaign_link)}>
                                                    <span className="mc-link-trunc">{row.campaign_link}</span>
                                                    <span className="mc-copy-icon">📋</span>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="mc-copy-pill refer" onClick={() => handleCopy(row.refer_link)}>
                                                    <span className="mc-link-trunc">{row.refer_link}</span>
                                                    <span className="mc-copy-icon">🔗</span>
                                                </div>
                                            </td>

                                            <td className="text-center_mc">
                                                <label className="mc-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={row.status === 'active'}
                                                        onChange={() => handleToggleStatus(row.id, row.status)}
                                                    />
                                                    <span className="mc-slider"></span>
                                                </label>
                                            </td>

                                            <td className="text-center_mc sticky-col_mc actions-cell_mc">
                                                <button className="mc-icon-btn edit" title="Edit" onClick={() => openEditModal(row)}>✏️</button>
                                                <button className="mc-icon-btn delete" title="Delete" onClick={() => handleDelete(row.id)}>🗑️</button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="mc-no-data">No campaigns found. Create one now!</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>

            {/* --- COMPLETE EDIT MODAL --- */}
            {isEditModalOpen && editData && (
                <div className="mc-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
                    <div className="mc-modal-box" onClick={e => e.stopPropagation()}>
                        <div className="mc-modal-header">
                            <h3>Edit Campaign</h3>
                            <button className="mc-close-btn" onClick={() => setIsEditModalOpen(false)}>&times;</button>
                        </div>

                        <div className="mc-modal-body">

                            {/* 1. Campaign Details */}
                            <div className="mc-section-block">
                                <h4>Campaign Details</h4>
                                <div className="mc-field-group">
                                    <label>Page Title</label>
                                    <input
                                        type="text"
                                        value={editData.title}
                                        onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                                        className="mc-glass-input"
                                    />
                                </div>
                                <div className="mc-field-group">
                                    <label>Telegram Link</label>
                                    <input
                                        type="text"
                                        value={editData.telegram}
                                        onChange={(e) => setEditData({ ...editData, telegram: e.target.value })}
                                        className="mc-glass-input"
                                    />
                                </div>
                            </div>

                            {/* 2. Refer Page Settings */}
                            <div className="mc-section-block">
                                <h4>Refer Page Settings</h4>
                                <div className="mc-row-2">
                                    <div className="mc-field-group">
                                        <label>Refer Page Name</label>
                                        <input
                                            type="text"
                                            value={editData.refer_name}
                                            onChange={(e) => setEditData({ ...editData, refer_name: e.target.value })}
                                            className="mc-glass-input"
                                        />
                                    </div>
                                    <div className="mc-field-group">
                                        <label>Refer Page Title</label>
                                        <input
                                            type="text"
                                            value={editData.refer_title}
                                            onChange={(e) => setEditData({ ...editData, refer_title: e.target.value })}
                                            className="mc-glass-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 3. Payout Configuration (UPDATED with PROFIT) */}
                            <div className="mc-section-block">
                                <h4>Payout Configuration</h4>
                                {editData.payouts.length > 0 ? (
                                    editData.payouts.map((evt, index) => {
                                        const limit = parseFloat(evt.limit) || 0;
                                        const userPay = parseFloat(evt.user) || 0;
                                        const referPay = parseFloat(evt.refer) || 0;

                                        // Dynamic Profit Calculation
                                        const profit = (limit - (userPay + referPay)).toFixed(2);

                                        return (
                                            <div key={index} className="mc-payout-row">
                                                <div className="mc-payout-header">
                                                    <span className="evt-name-label">{evt.name || evt.event_id}</span>
                                                    <span className="mc-limit-tag">Max Limit: ₹{limit}</span>
                                                </div>

                                                <div className="mc-inp-split">
                                                    <div className="mc-mini-group">
                                                        <label>User (₹)</label>
                                                        <input
                                                            type="number"
                                                            value={evt.user}
                                                            onChange={(e) => handlePayoutChange(index, 'user', e.target.value)}
                                                            className="mc-glass-input"
                                                        />
                                                    </div>
                                                    <div className="mc-mini-group">
                                                        <label>Refer (₹)</label>
                                                        <input
                                                            type="number"
                                                            value={evt.refer}
                                                            onChange={(e) => handlePayoutChange(index, 'refer', e.target.value)}
                                                            className="mc-glass-input"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="mc-profit-bar">
                                                    <span className="profit-label">Your Profit:</span>
                                                    <span className={`mc-profit-tag ${profit < 0 ? 'loss' : 'gain'}`}>
                                                        ₹{profit}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p className="mc-empty-text">No payout config available.</p>
                                )}
                            </div>

                            {/* 4. Domain Selection (Fixed: labelKey='url') */}
                            <div className="mc-section-block">
                                <h4>Domain Settings</h4>
                                <div className="mc-field-group">
                                    <label>Select Domain</label>
                                    <CustomSelect
                                        options={domains}
                                        value={editData.domain_id}
                                        onChange={(val) => setEditData({ ...editData, domain_id: val })}
                                        placeholder="Choose Domain"
                                        labelKey="domain"
                                        valueKey="id"
                                    />
                                </div>
                            </div>

                            <button className="mc-save-btn" onClick={handleEditSave} disabled={isSaving}>
                                {isSaving ? 'SAVING...' : 'SAVE CHANGES'}
                                <div className="mc-wave"></div>
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default MyCampaigns;